import * as React from 'react';
import { Button, Form, Row, message, DatePicker, Col } from 'antd';
import I18N from 'src/services/I18NService';
import WalletService, {
  IAccountInfo,
  EAccountMemberType,
  IAccountSupplement,
  PhotoOCRState,
} from 'src/services/WalletService';
import '../views/Wallet/wallet.css';
import { FormComponentProps, FormItemProps } from 'antd/lib/form';
import moment from 'moment';
import { IAgentInfo } from 'src/entity/AgentEntity';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox';
import FlexUpload from './FlexUpload';

interface IAccountSupplementFormProps extends FormComponentProps {
  userInfo?: IAgentInfo | IDrugStoreInfo;
  /** 开户账号信息 */
  account: IAccountInfo;
  /** 华通 - 要素完整性检查 */
  authenticateValid: boolean;
  /** 华通 - 影印完整性检查 */
  picturesValid: boolean;
  /** 通联 - 营业执照，组织机构代码张，税务登记证识别状态 */
  tonglianBusinessPhotocopyStatus: PhotoOCRState,
  /** 通联 - 法人身份证识别状态 */
  tonglianIdCardPhotocopyStatus: PhotoOCRState,
  /** 通联 - OCR失败错误原因 */
  tonglianOCRDetail?: string;
  /** 表单提交成功后关闭模态窗函数 */
  closeModal(): void;
}

interface IAccountSupplementFormState {
  /** 是否正在提交 */
  isLoading: boolean;
  disableEndDate: boolean;
}

/**
 * 表单提交数据
 */
interface IFormData {
  pic_id_card_front: string[],
  pic_id_card_back: string[],
  identity_begin_date: string,
  identity_end_date: string,
  pic_business_license?: string[],
  pic_tax_registration?: string[],
  pic_organization_code?: string[],
}

/**
 * 创建提现Form组件
 */
class CreateAccountSupplementForm extends React.Component<IAccountSupplementFormProps, IAccountSupplementFormState> {
  constructor(props: IAccountSupplementFormProps) {
    super(props);
    this.state = { isLoading: false, disableEndDate: false};
  }

  /**
   * 提交表单
   */
  handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const {
      form,
      account,
      userInfo,
      authenticateValid,
      picturesValid,
      tonglianBusinessPhotocopyStatus,
      tonglianIdCardPhotocopyStatus,
      closeModal,
    } = this.props;
  
    const isTonglian = account.depository_unit === 'TONGLIAN';

    form.validateFields(async (err, values: IFormData) => {
      if (!err) {
        const {
          pic_id_card_front,
          pic_id_card_back,
          identity_begin_date: beginDate,
          identity_end_date: endDate,
          pic_business_license,
          pic_organization_code,
          pic_tax_registration,
        } = values;
        const copyAccount = {...account, identity_begin_date: beginDate, identity_end_date: endDate};
        const completionInfo = WalletService.buildAccountcompletionInfo(account.member_type, copyAccount, userInfo);
        if (completionInfo) {
          const {address, telephone, profession_no, identity_begin_date, identity_end_date} = completionInfo;
          const idCardFrontPath = Array.isArray(pic_id_card_front) ? pic_id_card_front[0] : '';
          const idCardBackPath = Array.isArray(pic_id_card_back) ? pic_id_card_back[0] : '';
          const licensePath = Array.isArray(pic_business_license) ? pic_business_license[0] : '';
          const orgCodePath = Array.isArray(pic_organization_code) ? pic_organization_code[0] : '';
          const taxRegPath = Array.isArray(pic_tax_registration) ? pic_tax_registration[0] : '';
          const sumitParams: Partial<IAccountSupplement> = isTonglian ? {
            tonglian_business_photocopy: {
              pic_business_license: licensePath,
              pic_organization_code_certification: orgCodePath,
              pic_tax_registration_certification: taxRegPath,
            },
            tonglian_id_card_photocopy: {
              pic_id_card_front: idCardFrontPath,
              pic_id_card_back: idCardBackPath,
            }
          } : {
            authenticate_elements: {
              address,
              telephone,
              identity_begin_date,
              identity_end_date,
              profession_no,
            },
            pictures: {
              pic_id_card_front: idCardFrontPath,
              pic_id_card_back: idCardBackPath,
              pic_business_license: licensePath,
            }
          };
          if (isTonglian) {
            // 通联
            const buzPictureOk = this.PictureStatusOk(tonglianBusinessPhotocopyStatus);
            const idPictureOK = this.PictureStatusOk(tonglianIdCardPhotocopyStatus);
            if (buzPictureOk) {
              delete sumitParams.tonglian_business_photocopy;
            }
            if (idPictureOK) {
              delete sumitParams.tonglian_id_card_photocopy;
            }
          } else {
            // 华通
            if (authenticateValid) {
              delete sumitParams.authenticate_elements;
            }
            if (picturesValid) {
              delete sumitParams.pictures;
            } else if (account.member_type ===  EAccountMemberType.Individual && sumitParams.pictures) {
              // 个人用户 不传 pic_business_license 字段
              delete sumitParams.pictures.pic_business_license;
            }
          }
          this.setState({ isLoading: true });
          const res = await WalletService.updateAccountSupplement(copyAccount.id as string, sumitParams);
          if (!!res) {
            if (isTonglian) {
              message.success('影印件已提交，将在24小时内完成审核!');
            } else {
              message.success('提交成功');
            }
            closeModal();
          }
          this.setState({ isLoading: false });
        }
      }
    });
  };

  disabledStartDate = (fieldStartValue: moment.Moment | null) => {
    if (!fieldStartValue) {
      return false;
    }
    const fieldEndValue = this.props.form.getFieldValue('identity_end_date') as moment.Moment;
    // 计算零点时间
    const startValue = moment(fieldStartValue.format('YYYY-MM-DD'), 'YYYY-MM-DD')
    const endValue = fieldEndValue ? moment(fieldEndValue.format('YYYY-MM-DD'), 'YYYY-MM-DD') : moment('9999-12-31', 'YYYY-MM-DD');
    return startValue.valueOf() >= endValue.valueOf();
  };

  disabledEndDate = (fieldEndValue: moment.Moment | null) => {
    const fieldStartValue = this.props.form.getFieldValue('identity_begin_date');
    if (!fieldEndValue || !fieldStartValue) {
      return false;
    }
    // 计算零点时间
    const startValue = moment(fieldStartValue.format('YYYY-MM-DD'), 'YYYY-MM-DD');
    const endValue = moment(fieldEndValue.format('YYYY-MM-DD'), 'YYYY-MM-DD');
    return endValue.valueOf() <= startValue.valueOf();
  };

  handleEndDateCheck = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    this.props.form.setFieldsValue({'identity_end_date': null});
    this.setState({
      disableEndDate: checked,
    },  () => {
      this.props.form.validateFields(['identity_end_date'], { force: true });
    },);
  }

  endDateValidator = (rule: any, value: string, callback: any) => {
    if (!value && !this.state.disableEndDate) {
      callback('请输入证件有效截止日期');
    } else {
      callback();
    } 
  };

  render() {
    const { form, authenticateValid, tonglianOCRDetail, account } = this.props;
    const isEnterprise = account.member_type === EAccountMemberType.Enterprise;
    const isTonglian = account.depository_unit === 'TONGLIAN';
    const { isLoading, disableEndDate } = this.state;
    const { getFieldDecorator } = form;
    const formItemLayout: FormItemProps = {
      labelCol: { span: 7 },
      wrapperCol: { span: 15 },
    };

    const buzPicOK = isTonglian ? this.PictureStatusOk(this.props.tonglianBusinessPhotocopyStatus) : this.props.picturesValid;
    const idPicOK = isTonglian ? this.PictureStatusOk(this.props.tonglianIdCardPhotocopyStatus) : this.props.picturesValid;
    const idCardFrontPaths = account.pic_id_card_front && idPicOK ? [account.pic_id_card_front] : [];
    const idCardBackPaths = account.pic_id_card_back && idPicOK ? [account.pic_id_card_back] : [];
    const licensePaths = account.pic_business_license && buzPicOK ? [account.pic_business_license] : [];
    const orgCodePaths = account.pic_organization_code_certification && buzPicOK ? [account.pic_organization_code_certification] : [];
    const taxRegPaths = account.pic_tax_registration_certification && buzPicOK ? [account.pic_tax_registration_certification] : [];
    const isThreeLicense = account.enterprise_auth_type === 1;

    // const showFailedReason = (this.props.tonglianBusinessPhotocopyStatus === PhotoOCRState.OCRFailed ||
    //   this.props.tonglianIdCardPhotocopyStatus === PhotoOCRState.OCRFailed ) && isTonglian;

    return (
      <Form onSubmit={this.handleSubmit}>
        { /* 企业额外显示营业执照 */ !buzPicOK && isEnterprise && (
          <Form.Item label={'营业执照'} {...formItemLayout}  key={'pic_business_license'}>
            {getFieldDecorator(`pic_business_license`, {
              initialValue: licensePaths,
              rules: [
                {
                  required: true,
                  message: '请上传营业执照',
                },
              ],
            })(
              <FlexUpload
                disabled={buzPicOK}
                showText="营业执照"
              />
            )}
          </Form.Item>
        )}
        { /* 企业额外显示组织机构代码证 */ isThreeLicense && !buzPicOK && isEnterprise && isTonglian && (
          <Form.Item label={'组织机构代码证'} {...formItemLayout}  key={'pic_organization_code'}>
            {getFieldDecorator(`pic_organization_code`, {
              initialValue: orgCodePaths,
              rules: [
                {
                  required: true,
                  message: '请上传组织机构代码证',
                },
              ],
            })(
              <FlexUpload
                disabled={buzPicOK}
                showText="组织机构代码证"
              />
            )}
          </Form.Item>
        )}
        { /* 企业额外显示税务登记证 */ isThreeLicense && !buzPicOK && isEnterprise && isTonglian && (
          <Form.Item label={'税务登记证'} {...formItemLayout}  key={'pic_tax_registration'}>
            {getFieldDecorator(`pic_tax_registration`, {
              initialValue: taxRegPaths,
              rules: [
                {
                  required: true,
                  message: '请上传税务登记证',
                },
              ],
            })(
              <FlexUpload
                disabled={buzPicOK}
                showText="税务登记证"
              />
            )}
          </Form.Item>
        )}
        {!idPicOK && (<Row className="ant-form-item" style={{marginBottom: 0}}>
          <Col span={7} className="ant-form-item-label">
            <label className="ant-form-item-required">身份证</label>
          </Col>
          <Col span={15}>
            <Row type="flex" align="top" style={{flexWrap: 'nowrap'}}>
              <Form.Item style={{marginBottom: 0}}>
                {getFieldDecorator('pic_id_card_front', {
                  initialValue: idCardFrontPaths,
                  rules: [
                    {
                      required: true,
                      message: '请上传身份证人像面',
                    },
                  ],
                })(
                  <FlexUpload
                    disabled={idPicOK}
                    showText="人像面"
                  />
                )}
              </Form.Item>
              <Form.Item style={{marginLeft: 16, marginBottom: 0}}>
                {getFieldDecorator('pic_id_card_back', {
                  initialValue: idCardBackPaths,
                  rules: [
                    {
                      required: true,
                      message: '请上传身份证国徽面',
                    },
                  ],
                })(
                  <FlexUpload
                    disabled={idPicOK}
                    showText="国徽面"
                  />
                )}
              </Form.Item>
            </Row>
          </Col>
        </Row>)}

        {!isTonglian && !authenticateValid && (
          <React.Fragment>
            <Form.Item label={'证件有效开始日期'} {...formItemLayout}  key={'identity_start_date'}>
              {getFieldDecorator('identity_begin_date', {
                initialValue: null,
                rules: [
                  {
                    required: true,
                    message: '请填写证件有效开始日期',
                  },
                ],
              })(
                <DatePicker
                  style={{width: '100%'}}
                  showToday={false}
                  disabledDate={this.disabledStartDate}
                  format="YYYY-MM-DD"
                  placeholder="开始日期"
                />
              )}
            </Form.Item>

            <Form.Item required={true} label={'证件有效截止日期'} {...formItemLayout}  key={'identity_end_date'}>
              <Row type="flex" align="middle">
                {getFieldDecorator('identity_end_date', {
                  initialValue: null,
                  rules: [
                    {
                      validator: this.endDateValidator
                    },
                  ],
                })(
                  <DatePicker
                    disabled={disableEndDate}
                    style={{flex: '1 1 auto'}}
                    showToday={false}
                    disabledDate={this.disabledEndDate}
                    format="YYYY-MM-DD"
                    placeholder="截止日期"
                  />
                )}
                <Checkbox style={{marginLeft: 8}} checked={disableEndDate} onChange={this.handleEndDateCheck}>长期</Checkbox>
              </Row>
            </Form.Item>
          </React.Fragment>
        )}
          
        <Row gutter={24} style={{ margin: 0 }}>
          <Form.Item style={{ textAlign: 'center', margin: 0 }}>
            <Button type="primary" loading={isLoading} htmlType="submit">
              {I18N.get('common_ok')}
            </Button>
            <Button style={{marginLeft: 16}} onClick={this.props.closeModal}>
              {I18N.get('common_cancel')}
            </Button>
          </Form.Item>
        </Row>
      </Form>
    );
  }

  private PictureStatusOk(status: PhotoOCRState) {
    return status === PhotoOCRState.OCRSuccess || status === PhotoOCRState.OCRPending;
  }
}
export const AccountSupplementForm = Form.create<IAccountSupplementFormProps>()(CreateAccountSupplementForm);


// WEBPACK FOOTER //
// ./src/components/common/AccountSupplementForm.tsx